import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import MultiStepForm from './MultiStepForm';

/**
 * AnalysisView Component
 *
 * This component loads a saved analysis report from S3 and displays it.
 * It wraps the MultiStepForm component and injects the saved analysis results.
 */
const AnalysisView = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${config.API_URL}/api/reports/${reportId}`);
        setReportData(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching report:', err);
        setError(err.response?.data?.error || 'Failed to load report');
        setLoading(false);
      }
    };

    if (reportId) {
      fetchReport();
    }
  }, [reportId]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h2>Loading your report...</h2>
          <div className="chatbot-icons-loading" style={{ marginTop: '20px' }}>
            <img
              src="/images/ChatGPTIcon.svg"
              alt="ChatGPT"
              className="chatbot-icon-loading"
              style={{ animationDelay: '0s', width: '40px', height: '40px', margin: '0 10px' }}
            />
            <img
              src="/images/GeminiIcon.svg"
              alt="Gemini"
              className="chatbot-icon-loading"
              style={{ animationDelay: '0.5s', width: '40px', height: '40px', margin: '0 10px' }}
            />
            <img
              src="/images/GrokIcon.png"
              alt="Grok"
              className="chatbot-icon-loading"
              style={{ animationDelay: '1s', width: '40px', height: '40px', margin: '0 10px' }}
            />
            <img
              src="/images/PerplexityIcon.svg"
              alt="Perplexity"
              className="chatbot-icon-loading"
              style={{
                animationDelay: '1.5s',
                filter: 'brightness(0.3) contrast(1.5)',
                width: '40px',
                height: '40px',
                margin: '0 10px'
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '12px',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '20px' }}>Report Not Found</h2>
          <p style={{ marginBottom: '30px' }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Render the MultiStepForm with the saved analysis results
  // We'll pass the data as props and have MultiStepForm display it
  return (
    <MultiStepForm
      savedAnalysisResults={reportData?.analysisResults}
      savedBusinessData={reportData?.businessData}
      isViewingReport={true}
    />
  );
};

export default AnalysisView;
